// ANCHOR React
import { memo, useEffect, useState } from 'react';

// ANCHOR Linkify
import Linkify from 'react-linkify';

// ANCHOR Next
import Link from 'next/link';

// ANCHOR Base
import { Block } from 'baseui/block';
import { StyledLink } from 'baseui/link';
import { LabelMedium, LabelSmall, LabelXSmall } from 'baseui/typography';
import { styled } from 'baseui';
import { ChevronDown, ChevronUp } from 'baseui/icon';

// ANCHOR Models
import { ProviderData } from '@lyon/scoped-models/ProviderData';
import {
  CourseModulesData,
  useCourseModulesData,
} from '@lyon/scoped-models/CourseModulesData';
import { ProviderCheckManager } from '@lyon/scoped-models/ProviderCheckManager';
import { ConfirmationModalVisibility } from '@lyon/scoped-models/confirmation-modal/ConfirmationModalVisibility';
import {
  CourseStudentCheck,
  useCourseStudentCheckData,
  useCourseStudentCheckIsCourseStudent,
} from '@lyon/scoped-models/course-student/CourseStudentCheck';
import {
  CourseData,
  useCourseDataId,
  useCourseDataCoverImage,
  useCourseDataTitle,
  useCourseDataModuleCount,
  useCourseDataShare,
  useCourseDataDescription,
  useCourseDataPreviewVideo,
  useCourseDataLecturers,
  useCourseDataObjectives,
  useCourseDataRequirements,
  useCourseDataGains,
  useCourseDataCommunity,
} from '@lyon/scoped-models/CourseData';
import { MobileView } from '@lyon/scoped-models/MobileView';
import { CourseInstructorCheck, useCourseInstructorCheckData } from '@lyon/scoped-models/CourseInstructorCheck';

// ANCHOR Hooks
import { useConstantCallback } from '@lyonph/react-hooks';
import { useToggle } from '@lyon/utils/hooks/useToggle';

// ANCHOR Graph State
import { useAuthState } from '@lyon/nodes/firebase/auth/auth-state-node';
import { useAuthAdmin } from '@lyon/nodes/firebase/auth/auth-admin-node';

// ANCHOR Results
import { ICourseResult } from '@lyon/utils/api/results/course';
import { IProviderResult } from '@lyon/utils/api/results/provider';

// ANCHOR Utils
import { getCourseDescription } from '@lyon/utils/seo/get-course-details';
import { formatCountLabel } from '@lyon/utils/extras/formatCountLabel';
import { convertToSlug } from '@lyon/utils/extras/convertToSlug';
import { ucarecdn } from '@lyon/utils/ucarecdn';
import { track } from '@lyon/utils/segment';

// ANCHOR Components
import { constant } from '@lyon/components/utils/constant';
import { ContentCoverImage } from '@lyon/components/utils/ContentCoverImage';
import { ContentProviderInfo } from '@lyon/components/utils/ContentProviderInfo';
import { CategoryTag } from '@lyon/components/utils/CategoryTag';
import { Share } from '@lyon/components/share';
import { CourseBuyButton } from '@lyon/components/utils/CourseBuyButton';
import { Divider } from '@lyon/components/utils/Divider';
import { LionVideoPlayer } from '@lyon/components/utils/LionVideoPlayer';
import { Loader } from '@lyon/components/utils/Loader';
import { LyonEmptyState } from '@lyon/components/empty/LyonEmptyState';
import { LyonFooter } from '@lyon/components/footer';
import { LecturerList } from '@lyon/components/utils/LecturerList';
import { Button } from 'baseui/button';
import { THEME } from '@lyon/utils/theme';
import { LyonCourseContainer } from './LyonCourseContainer';
import { LyonCourseStartButton } from './LyonCourseStartButton';
import { LyonCourseBodyContainer } from './LyonCourseBodyContainer';
import { LyonCourseOutlinePanel } from './LyonCourseOutlinePanel';
import { LyonCourseObjectiveList } from './LyonCourseObjectiveList';
import { LyonCourseRequirementList } from './LyonCourseRequirementList';
import { LyonCourseGainList } from './LyonCourseGainList';

// ANCHOR Styles
import {
  H1,
  H2,
  NORMAL,
  PURPLE,
  HEADER_CONTAINER,
  HEADER_GRID,
  HEADER_TAGS,
  HEADER_TAG,
  FOOTER,
  NORMAL_FADED,
} from './styles';

interface IProps {
  id: string;
  initialData: ICourseResult;
  providerInitialData: IProviderResult;
}

const LyonCourseCoverImage = constant(() => {
  const title = useCourseDataTitle();
  const displayPhotoUuid = useCourseDataCoverImage();

  useEffect(() => {
    const scrollToDiv = document.getElementById('scroll-to-div');
    if (scrollToDiv) {
      scrollToDiv.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <Block position="relative">
      <Block
        id="scroll-to-div"
        position="absolute"
        bottom="140px"
      />
      <ContentCoverImage
        name={title}
        image={displayPhotoUuid}
        overrides={{
          Block: {
            style: {
              position: 'relative',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              objectFit: 'cover',

              [THEME.media.mini]: {
                minHeight: '156px',
              },
              [THEME.media.small]: {
                minHeight: '232px',
              },
              [THEME.media.medium]: {
                minHeight: '252px',
              },
              [THEME.media.large]: {
                minHeight: '284px',
              },
              [THEME.media.mlarge]: {
                minHeight: '364px',
              },
              [THEME.media.xlarge]: {
                minHeight: '384px',
              },
              [THEME.media.xxlarge]: {
                minHeight: '420px',
              },
              [THEME.media.xxxlarge]: {
                minHeight: '508px',
              },
            },
          },
        }}
      />
    </Block>
  );
});

const LyonCourseShare = constant(() => {
  const [id, title, course] = useCourseDataShare();
  const providerName = ProviderData.useSelector(({ data }) => data?.name);

  const description = course && providerName
    ? getCourseDescription(course, providerName)
    : null;

  if (id == null || title == null || description == null) {
    return null;
  }

  return (
    <Block marginBottom="16px">
      <Share
        url={`https://lyon.com.ph/course/${id}/${convertToSlug(title)}`}
        title={title}
        id={id}
        text={description}
        price={course?.price}
        createdAt={course?.createdAt}
        accountId={course?.providerId}
        imageUrl={course?.courseDisplayPhotoUuid}
      />
    </Block>
  );
});

const LyonCourseHeaderButtons = constant(() => {
  const isPurchased = useCourseStudentCheckData();
  const isStudent = useCourseStudentCheckIsCourseStudent();
  const isProvider = ProviderCheckManager.useSelector((state) => state.data);
  const isCourseInstructor = useCourseInstructorCheckData();

  const hasStartButton = !isProvider && !isCourseInstructor && isPurchased;
  const hasBuyButton = isProvider || isCourseInstructor || !isStudent;

  return (
    <>
      {hasStartButton && <LyonCourseStartButton />}
      {hasBuyButton && <CourseBuyButton />}
    </>
  );
});

const LyonCourseHeader = constant(() => {
  const user = useAuthState();
  const title = useCourseDataTitle();
  const courseId = useCourseDataId();
  const moduleCount = useCourseDataModuleCount();

  const [id, name, displayPhotoUuid] = ProviderData.useSelectors(({ data }) => [
    data?.id,
    data?.name,
    data?.displayPhotoUuid,
  ]);

  const isProvider = ProviderCheckManager.useSelector((state) => state.data);

  if (!courseId) {
    return null;
  }

  return (
    <Block overrides={HEADER_CONTAINER}>
      {
        isProvider && (
          <LabelXSmall marginBottom="12px">
            This is your course —
            {' '}
            <Link href={`/course/${courseId}/manage`} passHref>
              <StyledLink>
                Edit Course
              </StyledLink>
            </Link>
          </LabelXSmall>
        )
      }
      <Block overrides={HEADER_GRID}>
        <Block>
          <LabelMedium overrides={H1}>
            {title}
          </LabelMedium>
          <ContentProviderInfo
            id={id}
            name={name}
            displayPhotoUuid={displayPhotoUuid}
          />
        </Block>
        <Block marginTop="4px">
          <CourseStudentCheck.Provider
            courseId={courseId}
            userId={user?.uid}
          >
            <ConfirmationModalVisibility.Provider>
              <LyonCourseHeaderButtons />
            </ConfirmationModalVisibility.Provider>
          </CourseStudentCheck.Provider>
        </Block>
      </Block>
      <Block overrides={HEADER_GRID} marginTop="24px">
        <Block overrides={HEADER_TAGS}>
          <CategoryTag
            overrides={HEADER_TAG}
            label={`${formatCountLabel(moduleCount, 'module')}`}
          />
        </Block>
        <LyonCourseShare />
      </Block>
    </Block>
  );
});

interface IDividerProps {
  top?: string;
  bottom?: string;
}

const LyonCourseDivider = memo(({ top, bottom }: IDividerProps) => (
  <>
    <Block marginTop={top} />
    <Divider />
    <Block marginBottom={bottom} />
  </>
));

const LyonCourseModuleOutline = constant(() => {
  const modules = useCourseModulesData();

  if (!modules) {
    return <Loader />;
  }

  if (modules.length === 0) {
    return (
      <LyonEmptyState
        label="No modules."
        imgAlt="No modules."
      />
    );
  }

  return (
    <>
      {
        modules.map((module) => (
          <LyonCourseOutlinePanel
            key={module.id}
            data={module}
          />
        ))
      }
    </>
  );
});

const ClickableDiv = styled('div', {
  marginTop: '8px',
  background: 'none',
  cursor: 'pointer',
});

const MORE = <ChevronDown size={20} />;
const LESS = <ChevronUp size={20} />;

const COMPONENT_DECORATOR = (href: string, text: string, key: number) => (
  <StyledLink href={href} target="_blank" rel="noreferrer noopener" key={key}>
    {text}
  </StyledLink>
);

const LyonCourseBody = constant(() => {
  const description = useCourseDataDescription();
  const lecturers = useCourseDataLecturers();
  const objectives = useCourseDataObjectives();
  const gains = useCourseDataGains();
  const requirements = useCourseDataRequirements();
  const [lyonCommunity, communityDescription] = useCourseDataCommunity();
  const [posterId, videoId, title] = useCourseDataPreviewVideo();
  const isMobile = MobileView.useSelector((state) => state.isMobile);

  const hasRewards = gains && gains.length > 0;
  const hasObjectives = objectives && objectives.length > 0;
  const hasRequirements = requirements && requirements.length > 0;

  const [descriptionHeight, setDescriptionHeight] = useState(0);
  const { toggle, state: shouldTruncate } = useToggle(false);
  const descriptionRef = useConstantCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setDescriptionHeight(node.getBoundingClientRect().height);
    }
  });
  const isDescriptionLong = descriptionHeight >= 192;

  return (
    <LyonCourseBodyContainer>
      <Block>
        <LabelMedium overrides={H2}>
          About this Course
        </LabelMedium>
        <Linkify componentDecorator={COMPONENT_DECORATOR}>
          <LabelSmall
            overrides={(isDescriptionLong && !shouldTruncate)
              ? NORMAL_FADED
              : NORMAL}
            ref={descriptionRef}
          >
            {description}
          </LabelSmall>
        </Linkify>
        {isDescriptionLong && (
          <ClickableDiv onClick={toggle}>
            <LabelSmall overrides={PURPLE}>
              {`Show ${shouldTruncate ? 'less' : 'more'}`}
              {' '}
              {shouldTruncate ? LESS : MORE}
            </LabelSmall>
          </ClickableDiv>
        )}
        <LyonCourseDivider top="72px" bottom="56px" />
        {
          videoId && (
            <>
              <LabelMedium overrides={H2}>
                What to Expect
              </LabelMedium>
              <LionVideoPlayer
                title={title}
                videoId={videoId}
                posterId={posterId ?? ''}
              />
              <LyonCourseDivider top="72px" bottom="56px" />
            </>
          )
        }
        <LabelMedium overrides={H2}>
          Course Outline
        </LabelMedium>
        <LyonCourseModuleOutline />
        {isMobile && <LyonCourseDivider top="72px" bottom="56px" />}
      </Block>
      <Block>
        {
          lecturers && lecturers.length > 0 && (
            <>
              <LabelMedium overrides={H2}>
                {
                  formatCountLabel(lecturers.length, 'Instructor', {
                    includeCount: false,
                  })
                }
              </LabelMedium>
              <Block
                display="grid"
                gridRowGap="24px"
              >
                {
                  lecturers.map((lecturer) => (
                    <LecturerList
                      key={lecturer.id}
                      description={lecturer.description}
                      displayName={lecturer.name}
                      displayPhotoUuid={lecturer.displayPhotoUuid}
                    />
                  ))
                }
              </Block>
              {
                (hasObjectives || hasRequirements || hasRewards) && (
                  <LyonCourseDivider top="56px" bottom="48px" />
                )
              }
            </>
          )
        }
        {
          objectives && objectives.length > 0 && (
            <>
              <LabelMedium overrides={H2}>
                {
                  formatCountLabel(objectives.length, 'Objective', {
                    includeCount: false,
                  })
                }
              </LabelMedium>
              <LyonCourseObjectiveList list={objectives} />
              {
                (hasRequirements || hasRewards) && (
                  <LyonCourseDivider top="56px" bottom="48px" />
                )
              }
            </>
          )
        }
        {
          requirements && requirements.length > 0 && (
            <>
              <LabelMedium overrides={H2}>
                {
                  formatCountLabel(requirements.length, 'Requirement', {
                    includeCount: false,
                  })
                }
              </LabelMedium>
              <LyonCourseRequirementList list={requirements} />
              {hasRewards && <LyonCourseDivider top="56px" bottom="48px" />}
            </>
          )
        }
        {
          gains && gains.length > 0 && (
            <>
              <LabelMedium overrides={H2}>
                What You&apos;ll Get
              </LabelMedium>
              <LyonCourseGainList list={gains} />
              {lyonCommunity && <LyonCourseDivider top="56px" bottom="48px" />}
            </>
          )
        }
        {
          lyonCommunity && (
            <>
              <LabelMedium overrides={H2}>
                Join the Community
              </LabelMedium>
              <LabelMedium overrides={{
                Block: {
                  style: {
                    fontSize: '14px',
                    lineHeight: 1.65,
                    fontWeight: 400,
                    whiteSpace: 'pre-wrap',
                  },
                },
              }}
              >
                {communityDescription}
              </LabelMedium>
              <Block marginTop="40px">
                <StyledLink
                  href={lyonCommunity}
                  target="_blank"
                  rel="noreferrer noopener"
                  $style={{
                    textDecoration: 'none',
                  }}
                >
                  <Button type="button">
                    Go to Community
                  </Button>
                </StyledLink>
              </Block>
            </>
          )
        }
      </Block>
    </LyonCourseBodyContainer>
  );
});

export function LyonCourse({
  id,
  initialData: data,
  providerInitialData,
}: IProps) {
  const isAdmin = useAuthAdmin();

  useEffect(() => {
    track('Product Viewed', {
      productId: id,
      name: data.title,
      price: data.price,
      imageUrl: data.courseDisplayPhotoUuid
        ? ucarecdn(data.courseDisplayPhotoUuid)
        : undefined,
      url: `https://lyon.com.ph/course/${data.id}/${convertToSlug(data.title)}`,
    }, isAdmin, {
      context: {
        groupId: providerInitialData.id,
      },
    });
  }, [
    data.courseDisplayPhotoUuid, data.id, data.price, data.title,
    id, isAdmin, providerInitialData.id,
  ]);

  return (
    <ProviderCheckManager.Provider providerId={data.providerId}>
      <ProviderData.Provider
        id={providerInitialData.id}
        initialData={providerInitialData}
      >
        <CourseInstructorCheck.Provider courseId={id}>
          <CourseData.Provider id={id} initialData={data}>
            <CourseModulesData.Provider courseId={id}>
              <LyonCourseContainer>
                <LyonCourseCoverImage />
                <LyonCourseHeader />
                <LyonCourseBody />
                <Block overrides={FOOTER}>
                  <LyonCourseDivider top="192px" bottom="32px" />
                  <LyonFooter />
                </Block>
              </LyonCourseContainer>
            </CourseModulesData.Provider>
          </CourseData.Provider>
        </CourseInstructorCheck.Provider>
      </ProviderData.Provider>
    </ProviderCheckManager.Provider>
  );
}
